/**
 * Имена CSS-переменных, которые публикует `ViewportRuntime`.
 *
 * Это контракт с раскладками (Screen, BottomSheet, футеры) — переименовывать нельзя.
 * Префикс `--tg-` выбран не для красоты: `--tg-viewport-stable-height` Telegram пишет на
 * `<html>` сам, и наше значение намеренно совпадает с ним по смыслу. Разница в том, что у нас
 * оно есть и вне Telegram, и учитывает клавиатуру на iOS.
 *
 * `--tg-viewport-height` мы больше не публикуем: единственным её потребителем была шторка,
 * а шторке нужно не «окно минус подъём», а видимая полоса (`SHEET_ROOM_VAR`). Пользоваться
 * тем, что под этим именем пишет сам Telegram, нельзя — он не видит клавиатуру на iOS
 * (Telegram-iOS#1296); запрет держит `shared/ui/screenLayout.test.ts`.
 */
export const VIEWPORT_STABLE_HEIGHT_VAR = '--tg-viewport-stable-height'
/**
 * Полоса окна вебвью НИЖЕ видимой части. В Telegram на iOS окно выкладывается больше того,
 * что видно, и `position: fixed; bottom: 0` уезжает под нижний край экрана.
 */
export const VIEWPORT_BOTTOM_INSET_VAR = '--tg-viewport-bottom-inset'
/** Диагностика: на сколько iOS подтянул видимую полосу к полю. Раскладка им не пользуется. */
export const VIEWPORT_OFFSET_TOP_VAR = '--tg-viewport-offset-top'
/** Диагностика: сколько занимает клавиатура снизу окна. Раскладка им тоже не пользуется. */
export const KEYBOARD_INSET_VAR = '--tg-keyboard-inset'
/**
 * На сколько поднимается всё, что закреплено у нижнего края окна: футер экрана, меню-остров,
 * шторка. Единственная величина, которой пользуется раскладка, и единственное место, где она
 * считается, — `bottomLift.ts`.
 *
 * Префикс `--layout-`, а не `--tg-`: это не то, что сказала платформа, а наш вывод из её
 * замеров. Пересобирать её в CSS из `--tg-*` запрещено (`shared/ui/screenLayout.test.ts`):
 * ровно так кнопка дважды улетала выше клавиатуры — демпфер стоял на одном слагаемом,
 * а второе приходило сырым.
 */
export const BOTTOM_LIFT_VAR = '--layout-bottom-lift'
/**
 * Где стоит нижняя кромка ШТОРКИ, считая от низа окна.
 *
 * Отдельная величина, а не `--layout-bottom-lift`, потому что у шторки и у футера разные
 * системы отсчёта. Футер — тонкая полоса: пока клавиатура едет, он всё равно закрыт ею, и
 * его выгоднее двигать один раз по устоявшемуся замеру. Шторка занимает почти весь экран:
 * стоит ей на четверть секунды остаться у низа ОКНА, пока видимая полоса уже уехала вверх, —
 * и она проваливается ниже видимой области целиком. Поэтому шторка считается от нижней
 * кромки видимой полосы и получает поправку немедленно (docs/telegram.md §5.4).
 */
export const SHEET_LIFT_VAR = '--layout-sheet-lift'
/**
 * Видимое место над этой кромкой: по нему шторка меряет свою высоту. Пара к `SHEET_LIFT_VAR`
 * и считается тем же кадром — иначе коробка шторки поедет двумя движениями вместо одного.
 */
export const SHEET_ROOM_VAR = '--layout-sheet-room'
export const SAFE_AREA_TOP_VAR = '--tg-safe-area-top'
export const SAFE_AREA_BOTTOM_VAR = '--tg-safe-area-bottom'
export const CONTENT_SAFE_AREA_TOP_VAR = '--tg-content-safe-area-top'

/**
 * Порог, ниже которого сжатие вьюпорта считается не клавиатурой.
 * Вьюпорт ужимают ещё адресная строка браузера, панель автозаполнения и просто округление
 * дробных `visualViewport.height`. Самая низкая экранная клавиатура (iPhone SE, эмодзи-строка
 * скрыта) — около 216px, так что 80 отделяет одно от другого с большим запасом.
 */
export const KEYBOARD_MIN_INSET_PX = 80

/**
 * Сколько замер нижней кромки окна должен НЕ МЕНЯТЬСЯ, чтобы считаться устоявшимся.
 *
 * Клавиатура на iOS не появляется одним числом: сначала меняется высота видимой полосы, потом
 * WebKit подтягивает саму полосу к полю (`visualViewport.offsetTop`), и оба шага приезжают
 * отдельными событиями за время анимации (~250ms). Кто поднимается на первом же замере,
 * улетает выше нужного (полосу ещё не подтянули) и через кадр падает на место — тот самый
 * «отскок» кнопки. Поэтому подъём применяется один раз, когда геометрия перестала меняться.
 *
 * Отсчёт идёт от ПОСЛЕДНЕГО изменения, а не от первого. Будильник, заведённый от первого
 * замера, срабатывает вслепую: за кадр до поправки замер выглядит устоявшимся ровно так же,
 * как через час после неё, — и кнопка ехала дважды. Ждать не жалко: пока клавиатура едет,
 * кнопка всё равно закрыта ею.
 */
export const KEYBOARD_SETTLE_MIN_MS = 250

/**
 * Предохранитель: если размеры не успокаиваются вовсе (внешняя клавиатура, чужая анимация),
 * публикуем то, что намерили, а не держим кнопку внизу бесконечно. Отсчёт — от первого
 * разошедшегося замера.
 */
export const KEYBOARD_SETTLE_MAX_MS = 700
